import { useState, useRef, useCallback, useEffect } from "react";

/**
 * useSmoothTabZoom – Provides buttery-smooth animated zoom transitions
 * for dashboard tabs using requestAnimationFrame with ease-out cubic deceleration.
 *
 * @param {number} initialZoom Default zoom level (1.0)
 * @returns {{ tabZoom: number, targetTabZoom: number, handleTabZoomChange: Function, handleResetTabZoom: Function }}
 */
export function useSmoothTabZoom(initialZoom = 1) {
  const [tabZoom, setTabZoom] = useState(initialZoom);
  const targetTabZoomRef = useRef(initialZoom);
  const currentZoomRef = useRef(initialZoom);
  const tabZoomRafRef = useRef(null);

  // Keep ref synchronized with latest state
  useEffect(() => {
    currentZoomRef.current = tabZoom;
  }, [tabZoom]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tabZoomRafRef.current) {
        cancelAnimationFrame(tabZoomRafRef.current);
      }
    };
  }, []);

  const animateTabZoomTo = useCallback((target) => {
    const clampedTarget = Math.min(1.6, Math.max(0.5, Math.round(target * 10) / 10));
    targetTabZoomRef.current = clampedTarget;

    if (tabZoomRafRef.current) {
      cancelAnimationFrame(tabZoomRafRef.current);
    }

    const startZoom = currentZoomRef.current;
    if (Math.abs(startZoom - clampedTarget) < 0.002) {
      setTabZoom(clampedTarget);
      return;
    }

    const duration = 280; // ms – fluid, snappy matching canvas zoom
    const startTime = performance.now();

    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic: 1 - Math.pow(1 - progress, 3)
      const ease = 1 - Math.pow(1 - progress, 3);
      const interpolated = startZoom + (clampedTarget - startZoom) * ease;

      if (progress < 1) {
        setTabZoom(Math.round(interpolated * 1000) / 1000);
        tabZoomRafRef.current = requestAnimationFrame(step);
      } else {
        setTabZoom(clampedTarget);
        tabZoomRafRef.current = null;
      }
    };

    tabZoomRafRef.current = requestAnimationFrame(step);
  }, []);

  const handleTabZoomChange = useCallback(
    (delta) => {
      const currentTarget = targetTabZoomRef.current ?? currentZoomRef.current;
      const next = Math.round((currentTarget + delta) * 10) / 10;
      animateTabZoomTo(next);
    },
    [animateTabZoomTo]
  );

  const handleResetTabZoom = useCallback(() => {
    animateTabZoomTo(1);
  }, [animateTabZoomTo]);

  return {
    tabZoom,
    targetTabZoom: targetTabZoomRef.current,
    handleTabZoomChange,
    handleResetTabZoom,
  };
}

export default useSmoothTabZoom;
